import type { ShowLockedPeriods } from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { createContext, ReactNode, useContext, useMemo } from "react";
import {
    ScheduleLifecycleRequest,
    useScheduleTemplates,
    useWorkspaceScheduleApplications,
} from "@api/scheduleTemplate.api";
import { ScheduleApplicationListItem } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useScheduleLifecycleActions } from "@components/accounting/schedules/applications/useScheduleLifecycleActions";
import { MatchableItem } from "../types";
import { useInlineScheduleLockedPeriods } from "@components/accounting/schedules/useInlineScheduleLockedPeriods";

export interface MatchScheduleColumnValue {
    isEnabled: boolean;
    /** The row the drawer was opened for; every selected row links to it. */
    sourceItem: MatchableItem | null;
    selectedItems: MatchableItem[];
    templates: ScheduleTemplateListItem[];
    applications: ScheduleApplicationListItem[];
    requestLifecycleAction:
        | ((request: ScheduleLifecycleRequest) => void)
        | undefined;
    showLockedPeriods: ShowLockedPeriods;
}

const DISABLED: MatchScheduleColumnValue = {
    isEnabled: false,
    sourceItem: null,
    selectedItems: [],
    templates: [],
    applications: [],
    requestLifecycleAction: undefined,
    showLockedPeriods: () => false,
};

const MatchScheduleColumnCtx =
    createContext<MatchScheduleColumnValue>(DISABLED);

export function useMatchScheduleColumn(): MatchScheduleColumnValue {
    return useContext(MatchScheduleColumnCtx);
}

interface ProviderProps {
    sourceItem: MatchableItem | null;
    selectedItems: MatchableItem[];
    children: ReactNode;
}

/**
 * Feeds the Schedule column of every AccrualMatchTable in a drawer and hosts
 * the modals its actions open, all as local nodes (see
 * `useInlineScheduleLockedPeriods` for why).
 */
export const MatchScheduleColumnProvider = ({
    sourceItem,
    selectedItems,
    children,
}: ProviderProps) => {
    const { schedules: isEnabled } = useTypedFlags();
    const { data: templates } = useScheduleTemplates({ enabled: isEnabled });
    const { applications } = useWorkspaceScheduleApplications(
        {},
        { enabled: isEnabled },
    );
    const { showLockedPeriods, lockedPeriodsModal } =
        useInlineScheduleLockedPeriods();
    const { requestLifecycleAction, modals: lifecycleModals } =
        useScheduleLifecycleActions({ showLockedPeriods });

    const value = useMemo<MatchScheduleColumnValue>(
        () => ({
            isEnabled,
            sourceItem,
            selectedItems,
            templates: templates ?? [],
            applications: applications ?? [],
            requestLifecycleAction,
            showLockedPeriods,
        }),
        [
            isEnabled,
            sourceItem,
            selectedItems,
            templates,
            applications,
            requestLifecycleAction,
            showLockedPeriods,
        ],
    );

    return (
        <MatchScheduleColumnCtx.Provider value={value}>
            {children}
            {isEnabled && lifecycleModals}
            {lockedPeriodsModal}
        </MatchScheduleColumnCtx.Provider>
    );
};
